import { CharReveal, CountUp, formatInt, formatMinutes } from '../index.js';

import type { StyleWithVars } from '../index.js';
import type { ChannelId, PartyWrapped, WrappedChannel } from '@mixwire/contracts';
import type { ReactElement } from 'react';

type Slide04TopArtistProps = {
  readonly wrapped: PartyWrapped;
};

const SUMMARY_STYLE: StyleWithVars = { '--d': '1500' };
const FIRST_DOT_DELAY_MS = 1200;
const DOT_STAGGER_MS = 120;

const colourOf = (channels: readonly WrappedChannel[], slotNumber: ChannelId): string =>
  channels.find((channel) => channel.slotNumber === slotNumber)?.colour ?? 'currentcolor';

const reachOf = (slotCount: number): string => {
  if (slotCount >= 3) {
    return 'all three channels';
  }

  return slotCount === 2 ? 'two channels' : 'one channel';
};

export const Slide04TopArtist = ({ wrapped }: Slide04TopArtistProps): ReactElement => {
  const { topArtist, channels } = wrapped;

  if (topArtist === null) {
    return (
      <>
        <p className="lede rise">The artist the decks kept coming back to</p>
        <h2 className="big">
          <CharReveal text="Nobody, yet" delayMs={400} />
        </h2>
        <p className="lede rise" style={SUMMARY_STYLE}>
          Not enough played for one artist to stand out from the rest.
        </p>
      </>
    );
  }

  return (
    <>
      <p className="lede rise">
        {topArtist.plays === 1
          ? 'The only artist on the decks'
          : 'The artist the decks kept coming back to'}
      </p>
      <h2 className="big">
        <CharReveal text={topArtist.name} delayMs={400} />
      </h2>
      <div className="dots" aria-hidden="true">
        {topArtist.slots.map((slotNumber, index) => {
          const dotStyle: StyleWithVars = {
            '--d': String(FIRST_DOT_DELAY_MS + index * DOT_STAGGER_MS),
            '--c': colourOf(channels, slotNumber),
          };

          return <i className="pop" key={slotNumber} style={dotStyle} />;
        })}
      </div>
      <p className="lede rise" style={SUMMARY_STYLE}>
        <b>
          <CountUp to={topArtist.plays} format={formatInt} delayMs={1500} />
        </b>{' '}
        {topArtist.plays === 1 ? 'play' : 'plays'} across {reachOf(topArtist.slots.length)},{' '}
        <b>
          <CountUp to={topArtist.ms} format={formatMinutes} delayMs={1500} />
        </b>{' '}
        minutes in total.
      </p>
    </>
  );
};
